import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Divider,
  Chip,
  Stack,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApiGet } from '../../../utils/httpClientTs';
import { getIntroductions } from '../../../endpoints/introduction';
import { getSpds } from '../../../endpoints/annualReports';
import { formatSpdLabel } from '../../../utils/spdLabel';
import { IntroductionMinimal } from '../../../types/introduction';
import ServerSideTable from '../../../Components/ServerSideTable';
import { SortOrder } from '../../../types/table';
import SPDSelector from '../../../Components/SPDSelector';
import dayjs from 'dayjs';

const IntroductionList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (!value || value === '') {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const [searchTerm, setSearchTerm] = useState(
    () => searchParams.get('search') || ''
  );
  const [debouncedSearch, setDebouncedSearch] = useState(
    () => searchParams.get('search') || ''
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] =
    useState<keyof IntroductionMinimal>('first_name');
  const [spd, setSpd] = useState(() => searchParams.get('spd') || '');
  const [showFilters, setShowFilters] = useState(false);

  // Track previous values to prevent loops
  const prevSearch = useRef(searchTerm);
  const prevSpd = useRef(spd);

  // Fetch SPD data for display names
  const { data: spdList } = useApiGet(
    ['spds', true],
    () => getSpds({ all: true }),
    {
      enabled: true,
      retry: 1,
    }
  );

  // Handle URL parameter changes (only when URL changes externally)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlSpd = searchParams.get('spd') || '';

    // Update state from URL if different
    if (urlSearch !== searchTerm) {
      setSearchTerm(urlSearch);
      setDebouncedSearch(urlSearch);
    }
    if (urlSpd !== spd) {
      setSpd(urlSpd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams to avoid loops

  // Debounce search term and update URL
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update URL when search changes
  useEffect(() => {
    if (prevSearch.current !== searchTerm) {
      prevSearch.current = searchTerm;
      const urlSearch = searchParams.get('search') || '';
      if (searchTerm !== urlSearch) {
        updateSearchParams('search', searchTerm);
      }
    }
  }, [searchTerm, updateSearchParams, searchParams]);

  // Update URL when SPD filter changes and reset pagination
  useEffect(() => {
    if (prevSpd.current !== spd) {
      prevSpd.current = spd;
      const urlSpd = searchParams.get('spd') || '';
      if (spd !== urlSpd) {
        updateSearchParams('spd', spd);
        setPage(0); // Reset to first page when filter changes
      }
    }
  }, [spd, updateSearchParams, searchParams]);

  const {
    data: introductionsData,
    isLoading,
    error,
  } = useApiGet(
    ['introductions', page, rowsPerPage, debouncedSearch, order, orderBy, spd],
    () =>
      getIntroductions({
        page: page + 1, // API uses 1-based indexing
        per_page: rowsPerPage,
        search: debouncedSearch || undefined,
        spd_chapter_id: spd || undefined,
      })
  );

  const handleCreateIntroduction = () => {
    navigate('/introductions/create');
  };

  const handleViewIntroduction = (introduction: IntroductionMinimal) => {
    navigate(`/introductions/${introduction.id}`);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSort = (property: keyof IntroductionMinimal) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  // Helper function to get SPD display name
  const getSpdDisplayName = (spdId: string) => {
    if (!spdId || !spdList) return spdId;
    const spdItem = (spdList as any[]).find((spd) => spd.id === spdId);
    return spdItem ? formatSpdLabel(spdItem) : spdId;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setSpd('');
    setShowFilters(false);
    setPage(0);
    // Clear URL parameters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('search');
    newSearchParams.delete('spd');
    setSearchParams(newSearchParams);
  };

  const hasActiveFilters = searchTerm || spd;

  const columns = [
    {
      id: 'first_name' as keyof IntroductionMinimal,
      label: 'Name',
      sortable: false,
      render: (value: any, introduction: IntroductionMinimal) =>
        `${introduction.first_name} ${introduction.last_name}`,
    },
    {
      id: 'country' as keyof IntroductionMinimal,
      label: 'Location',
      sortable: false,
      render: (value: any, introduction: IntroductionMinimal) =>
        [introduction.city, introduction.state, introduction.country]
          .filter(Boolean)
          .join(', '),
    },
    {
      id: 'created_at' as keyof IntroductionMinimal,
      label: 'Submitted',
      sortable: false,
      render: (value: any, introduction: IntroductionMinimal) =>
        introduction.created_at
          ? dayjs(introduction.created_at).format('MM/DD/YYYY')
          : 'N/A',
    },
  ];

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Error loading introductions: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Non-Member Intro
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: 'italic', mb: 1 }}
        >
          (Formerly Introduction of a Woman)
        </Typography>
        <Typography variant="subtitle1" component="p" color="text.secondary">
          Manage non-member introductions to other chapters. Create new
          introductions or view existing ones.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateIntroduction}
            sx={{
              backgroundColor: '#00A859',
              '&:hover': { backgroundColor: '#008a4a' },
            }}
          >
            Create Non-Member Intro
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search"
              id="search-field"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Button
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              fullWidth
              size="small"
            >
              Filters
            </Button>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              fullWidth
              size="small"
            >
              Clear
            </Button>
          </Grid>
        </Grid>

        {/* Advanced Filters */}
        {showFilters && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SPDSelector
                  spd={spd}
                  handleSpdSelect={setSpd}
                  keyId="introduction-list"
                  all={true}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Active Filters:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {searchTerm && (
              <Chip
                label={`Search: "${searchTerm}"`}
                onDelete={() => {
                  setSearchTerm('');
                  setDebouncedSearch('');
                }}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {spd && (
              <Chip
                label={`SPD: ${getSpdDisplayName(spd)}`}
                onDelete={() => setSpd('')}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* Table */}
      <Paper sx={{ p: 2 }}>
        <ServerSideTable
          data={introductionsData?.data || []}
          columns={columns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={introductionsData?.pagination?.total_count || 0}
          totalPages={introductionsData?.pagination?.total_pages || 0}
          hasNext={introductionsData?.pagination?.has_next || false}
          hasPrev={introductionsData?.pagination?.has_prev || false}
          order={order}
          orderBy={String(orderBy)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={handleSort}
          onRowClick={handleViewIntroduction}
          emptyMessage="No introductions found. Create your first introduction to get started."
        />
      </Paper>
    </Box>
  );
};

export default IntroductionList;
